import { isValidElement } from "react";
import { useShallowEffect } from "@mantine/hooks";
import { Form, Formik, FormikValues, useFormikContext } from "formik";
import type { ISteppedFlowFormikFormProps, ISteppedFlowFormikObserverProps } from "../stepped-flow.types";
import { SteppedFlowStepScope } from "../stepped-flow-step-scope";

/**
 * Rendered inside the form to push the live Formik state up to the shell's footer buttons, which sit
 * outside the form (so the shell keeps Mantine's flex layout). Cleared on unmount.
 */
const SteppedFlowFormikObserver = <Values extends FormikValues = FormikValues>({
    onFormikChange,
}: ISteppedFlowFormikObserverProps<Values>) => {
    const formik = useFormikContext<Values>();
    useShallowEffect(() => {
        onFormikChange(formik);
        return () => onFormikChange(null);
    }, [formik, onFormikChange]);
    return null;
};

/**
 * The form a Formik stepped flow owns, rendered in the shell's body. A single Formik wraps every step, so
 * form state persists while navigating between steps and is reset when the shell closes (the body unmounts).
 * Takes `formState` from `useSteppedFlowFormik` plus the caller's Formik config.
 */
const SteppedFlowFormikForm = <Values extends FormikValues = FormikValues>({
    steps,
    active,
    validationSchema,
    registerValidation,
    onFormikChange,
    initialValues,
    onSubmit,
    enableReinitialize,
    validateOnMount,
}: ISteppedFlowFormikFormProps<Values>) => (
    <Formik<Values>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={enableReinitialize}
        validateOnMount={validateOnMount}
    >
        <Form>
            <SteppedFlowFormikObserver<Values> onFormikChange={onFormikChange} />
            {steps.map((step, index) => (
                <SteppedFlowStepScope
                    key={index}
                    index={index}
                    isActive={index === active}
                    registerValidation={registerValidation}
                >
                    {/* Rendered for every step, active or not, so the whole-form schema is
                        complete before the user has visited each step. Renders null. */}
                    {isValidElement(step.props.validation) && step.props.validation}
                    {step}
                </SteppedFlowStepScope>
            ))}
        </Form>
    </Formik>
);

export { SteppedFlowFormikForm };
